import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  SUBMISSION_STATUSES,
  RESEARCH_FIELDS,
} from '../../constants/researchSupport';
import {
  fetchAllSubmissions,
  fetchSubmissionById,
  updateSubmissionStatus,
} from '../../services/researchSubmissionService';
import CloudinaryDocumentViewer from '../research-support/CloudinaryDocumentViewer';

const ITEMS_PER_PAGE = 10;

function exportCsv(rows) {
  const headers = [
    'id',
    'fullName',
    'email',
    'institution',
    'country',
    'researchField',
    'researchTitle',
    'status',
    'submittedAt',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = String(r[h] ?? '').replace(/"/g, '""');
          return `"${val}"`;
        })
        .join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `research-submissions-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function ResearchSupportManagement() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [fieldFilter, setFieldFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [reviewerNotes, setReviewerNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAllSubmissions();
      setSubmissions(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, fieldFilter, search]);

  const filtered = useMemo(() => {
    return submissions.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (fieldFilter !== 'all' && s.researchField !== fieldFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return (
          s.email?.toLowerCase().includes(q) ||
          s.fullName?.toLowerCase().includes(q) ||
          s.researchTitle?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [submissions, statusFilter, fieldFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const pageItems = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const metrics = useMemo(() => {
    const byStatus = {};
    const byCountry = {};
    submissions.forEach((s) => {
      byStatus[s.status] = (byStatus[s.status] || 0) + 1;
      byCountry[s.country] = (byCountry[s.country] || 0) + 1;
    });
    return { total: submissions.length, byStatus, byCountry };
  }, [submissions]);

  const openDetail = (sub) => {
    setSelected(sub);
    setReviewerNotes(sub.reviewerNotes || '');
    setNewStatus(sub.status);
    setNotifyApplicant(true);
  };

  const handleStatusSave = async () => {
    if (!selected || !newStatus) return;
    setSaving(true);
    try {
      await updateSubmissionStatus({
        submissionId: selected.id,
        status: newStatus,
        reviewerNotes,
        note: `Status changed to ${newStatus}`,
        actorEmail: user?.email,
        notifyApplicant,
      });
      const refreshed = await fetchSubmissionById(selected.id);
      if (refreshed) setSelected(refreshed);
      await load();
    } catch (e) {
      alert(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-10 w-10 border-b-2 border-iof rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Research Support Submissions</h1>
          <p className="text-gray-600 text-sm">
            Review applications, preview documents, and notify applicants by email.
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportCsv(filtered)}
          className="px-4 py-2 bg-iof text-white rounded-md text-sm hover:bg-iof-dark"
        >
          Export CSV ({filtered.length})
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total" value={metrics.total} />
        <MetricCard
          label="Under review"
          value={metrics.byStatus[SUBMISSION_STATUSES.UNDER_REVIEW] || 0}
        />
        <MetricCard
          label="Accepted"
          value={metrics.byStatus[SUBMISSION_STATUSES.ACCEPTED] || 0}
        />
        <MetricCard label="Countries" value={Object.keys(metrics.byCountry).length} />
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Search name, email, title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-md border-gray-300 text-sm focus:border-iof focus:ring-iof min-w-[200px]"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border-gray-300 text-sm"
        >
          <option value="all">All statuses</option>
          {Object.values(SUBMISSION_STATUSES)
            .filter((s) => s !== SUBMISSION_STATUSES.DRAFT)
            .map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
        </select>
        <select
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          className="rounded-md border-gray-300 text-sm"
        >
          <option value="all">All fields</option>
          {RESEARCH_FIELDS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </div>

      <div className="grid xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-4">
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Applicant</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageItems.map((sub) => (
                  <tr
                    key={sub.id}
                    className={`hover:bg-gray-50 ${
                      selected?.id === sub.id ? 'bg-iof/5' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{sub.fullName}</p>
                      <p className="text-xs text-gray-500">{sub.email}</p>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <p className="truncate">{sub.researchTitle}</p>
                      <p className="text-xs text-gray-500">{sub.researchField}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${
                          STATUS_COLORS[sub.status] || 'bg-gray-100'
                        }`}
                      >
                        {STATUS_LABELS[sub.status] || sub.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openDetail(sub)}
                        className="text-iof hover:underline"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p className="p-8 text-center text-gray-500">No submissions match your filters.</p>
            )}
          </div>

          {filtered.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filtered.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {selected ? (
          <aside className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 h-fit xl:sticky xl:top-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-semibold text-gray-900">{selected.researchTitle}</h2>
            <p className="text-xs text-gray-500">
              {selected.fullName} · {selected.email}
              <br />
              {selected.institution}, {selected.country}
            </p>
            <p className="text-sm text-gray-600 line-clamp-6">{selected.abstract || 'nill'}</p>

            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              {[
                ['Email', selected.email],
                ['Phone', selected.phone],
                ['Institution', selected.institution],
                ['Department', selected.department],
                ['Level of study', selected.levelOfStudy],
                ['Country', selected.country],
                ['Graduation year', selected.graduationYear],
                ['Research field', selected.researchField],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-medium text-gray-500">{label}</p>
                  <p className="text-sm text-gray-800">{value || 'nill'}</p>
                </div>
              ))}
            </div>

            <div className="grid gap-4 sm:grid-cols-2 mt-4">
              {[
                ['Keywords', selected.keywords],
                ['Publication goal', selected.publicationGoal],
                ['LinkedIn / profile', selected.linkedIn],
                ['Thesis PDF file', selected.files?.thesisPdf?.originalName],
                ['Supporting docs file', selected.files?.supportingDocs?.originalName],
                ['Dataset file', selected.files?.dataset?.originalName],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-medium text-gray-500">{label}</p>
                  <p className="text-sm text-gray-800">{value || 'nill'}</p>
                </div>
              ))}
            </div>

            <CloudinaryDocumentViewer
              fileMeta={selected.files?.thesisPdf}
              label="Thesis / project PDF"
            />
            {selected.files?.supportingDocs?.url ? (
              <CloudinaryDocumentViewer
                fileMeta={selected.files.supportingDocs}
                label="Supporting documents"
              />
            ) : (
              <p className="text-sm text-gray-600 p-4 bg-gray-50 rounded-lg border border-gray-200">
                Supporting documents: nill
              </p>
            )}
            {selected.files?.dataset?.url ? (
              <CloudinaryDocumentViewer
                fileMeta={selected.files.dataset}
                label="Dataset"
              />
            ) : (
              <p className="text-sm text-gray-600 p-4 bg-gray-50 rounded-lg border border-gray-200">
                Dataset: nill
              </p>
            )}

            <div className="text-xs bg-gray-50 border border-gray-200 rounded p-3">
              <p className="font-medium text-gray-800 mb-1">Emails sent</p>
              {selected.emailHistory?.length > 0 ? (
                <ul className="space-y-1 text-gray-600">
                  {selected.emailHistory.slice(-4).map((e, i) => (
                    <li key={`${e.type}-${i}`}>
                      <span className="font-semibold">{e.type}</span> → {e.to}{' '}
                      <span className="text-gray-400">
                        {e.sentAt ? new Date(e.sentAt).toLocaleString() : 'unknown time'}
                      </span>
                      {e.error && (
                        <div className="text-[10px] text-red-600 mt-1">Error: {e.error}</div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600">
                  No email history yet. If the submission was created successfully, email delivery may not have been triggered or the Cloud Function may not have executed.
                </p>
              )}
              {selected.lastEmailAt && (
                <p className="mt-2 text-gray-500 text-[11px]">
                  Last email recorded: {new Date(selected.lastEmailAt).toLocaleString()}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full rounded-md border-gray-300 text-sm"
              >
                {Object.values(SUBMISSION_STATUSES)
                  .filter((s) => s !== SUBMISSION_STATUSES.DRAFT)
                  .map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reviewer notes (shown to applicant when emailed)
              </label>
              <textarea
                rows={4}
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                className="w-full rounded-md border-gray-300 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={notifyApplicant}
                onChange={(e) => setNotifyApplicant(e.target.checked)}
                className="rounded border-gray-300 text-iof focus:ring-iof"
              />
              Email applicant about this update
            </label>
            <button
              type="button"
              onClick={handleStatusSave}
              disabled={saving}
              className="w-full py-2 bg-iof text-white rounded-md hover:bg-iof-dark disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save & notify'}
            </button>
          </aside>
        ) : (
          <aside className="hidden xl:block bg-gray-50 border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500 text-sm">
            Select a submission to preview documents and update status.
          </aside>
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-iof-dark mt-1">{value}</p>
    </div>
  );
}

function Pagination({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <p className="text-gray-600">
        Showing {(currentPage - 1) * itemsPerPage + 1}–
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Previous
        </button>
        <span className="px-2 py-1 text-gray-700">
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="px-3 py-1 border rounded disabled:opacity-40 hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default ResearchSupportManagement;
